/* ==========================================================
   AQUA Theme JS — Vanilla, no framework, no CDN
   ========================================================== */

'use strict';

/* ─── Accordéon accessible ─────────────── */
class AquaAccordion {
  constructor(el) {
    this.btns = el.querySelectorAll('.aqua-accordion__btn');
    this.btns.forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }
}

/* ─── Cart Drawer ───────────────────────── */
class AquaCartDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = document.getElementById('aqua-cart-overlay');
    this.drawer  = document.getElementById('aqua-cart-drawer-inner');
    this.closeBtn = this.querySelector('[data-close]');
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    if (this.overlay) this.overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.refreshCart();
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    if (this.overlay) this.overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  async refreshCart() {
    try {
      const res = await fetch('/cart.json');
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (e) {}
  }
}
customElements.define('aqua-cart-drawer', AquaCartDrawer);

/* ─── Add To Cart ───────────────────────── */
class AquaAddToCart extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form') || this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', e => this.handleSubmit(e));
    this.btn = this.querySelector('[name="add"]') || this.querySelector('.aqua-atc-btn');
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (!this.btn) return;

    const originalText = this.btn.textContent;
    this.btn.disabled = true;
    this.btn.textContent = 'Ajout…';

    try {
      const formData = new FormData(this.form);
      const body = JSON.stringify({ items: [{ id: formData.get('id'), quantity: parseInt(formData.get('quantity') || 1, 10) }] });
      const res  = await fetch('/cart/add.json', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
      if (!res.ok) throw new Error('Cart add failed');
      document.dispatchEvent(new Event('cart:open'));
    } catch (err) {
      this.btn.textContent = 'Erreur — Réessayer';
      setTimeout(() => { this.btn.textContent = originalText; this.btn.disabled = false; }, 2000);
      return;
    }

    this.btn.textContent = '✓ Ajouté';
    setTimeout(() => { this.btn.textContent = originalText; this.btn.disabled = false; }, 1500);
  }
}
customElements.define('aqua-add-to-cart', AquaAddToCart);

/* ─── Sticky ATC mobile ─────────────────── */
class AquaStickyAtc extends HTMLElement {
  connectedCallback() {
    const target = document.querySelector('.aqua-product-info__atc');
    if (!target) return;
    const obs = new IntersectionObserver(([entry]) => {
      this.classList.toggle('is-visible', !entry.isIntersecting);
      this.setAttribute('aria-hidden', String(entry.isIntersecting));
    }, { threshold: 0 });
    obs.observe(target);
    const btn = this.querySelector('button');
    if (btn) btn.addEventListener('click', () => {
      document.querySelector('.aqua-product-info__atc button[name="add"]')?.click();
    });
  }
}
customElements.define('aqua-sticky-atc', AquaStickyAtc);

/* ─── Variant selector ──────────────────── */
class AquaVariantSelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', () => this.onVariantChange());
  }

  onVariantChange() {
    const form = this.closest('form');
    if (!form) return;
    const options = [];
    this.querySelectorAll('[data-option-index]').forEach(el => {
      const inputs = el.querySelectorAll('input[type="radio"]:checked, select');
      inputs.forEach(inp => options.push(inp.value));
    });
    const productData = JSON.parse(this.closest('[data-product-json]')?.dataset.productJson || '{}');
    const variant = productData.variants?.find(v => {
      return options.every((opt, i) => v[`option${i + 1}`] === opt);
    });
    if (!variant) return;
    form.querySelector('[name="id"]').value = variant.id;
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));
    const priceEl = form.closest('.aqua-product-info')?.querySelector('.aqua-price__current');
    if (priceEl) priceEl.textContent = (variant.price / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
    const atcBtn = form.querySelector('button[name="add"]');
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      const priceTag = atcBtn.querySelector('.aqua-btn__price');
      if (priceTag) priceTag.textContent = '· ' + (variant.price / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
    }
  }
}
customElements.define('aqua-variant-selector', AquaVariantSelector);

/* ─── Quantity input ────────────────────── */
class AquaQuantityInput extends HTMLElement {
  connectedCallback() {
    this.input  = this.querySelector('input');
    this.minusBtn = this.querySelector('[name="minus"]');
    this.plusBtn  = this.querySelector('[name="plus"]');
    if (this.minusBtn) this.minusBtn.addEventListener('click', () => this.adjust(-1));
    if (this.plusBtn)  this.plusBtn.addEventListener('click',  () => this.adjust(+1));
  }
  adjust(delta) {
    const val = parseInt(this.input.value || 1, 10);
    const min = parseInt(this.input.min || 1, 10);
    const max = parseInt(this.input.max || 99, 10);
    this.input.value = Math.max(min, Math.min(max, val + delta));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
customElements.define('aqua-quantity-input', AquaQuantityInput);

/* ─── Gallery ───────────────────────────── */
class AquaGallery extends HTMLElement {
  connectedCallback() {
    this.mainImg  = this.querySelector('.aqua-gallery__main-img');
    this.thumbs   = this.querySelectorAll('.aqua-gallery__thumb');
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src  = thumb.dataset.full;
        const alt  = thumb.dataset.alt;
        if (this.mainImg && src) { this.mainImg.src = src; this.mainImg.alt = alt || ''; }
        this.thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }
}
customElements.define('aqua-gallery', AquaGallery);

/* ─── Quick-buy overlay ─────────────────── */
class AquaQuickBuy extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-quick-buy-trigger]');
    this.overlay = this.querySelector('.aqua-quick-buy__overlay');
    this.close   = this.querySelector('[data-close]');
    if (this.trigger) this.trigger.addEventListener('click', e => { e.preventDefault(); this.open(); });
    if (this.close)   this.close.addEventListener('click', () => this.closePanel());
    if (this.overlay) this.overlay.addEventListener('click', () => this.closePanel());
  }
  open() {
    this.setAttribute('aria-expanded', 'true');
    this.querySelector('.aqua-quick-buy__panel')?.removeAttribute('hidden');
  }
  closePanel() {
    this.setAttribute('aria-expanded', 'false');
    this.querySelector('.aqua-quick-buy__panel')?.setAttribute('hidden', '');
  }
}
customElements.define('aqua-quick-buy', AquaQuickBuy);

/* ─── Init ──────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-accordion]').forEach(el => new AquaAccordion(el));
});
